import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, authHeaders } from "@/lib/leuteliaApi";
import { Button } from "@/components/ui/button";
import { BookOpen, Download, Loader2, RefreshCw, Sparkles } from "lucide-react";

const Overline = ({ children }) => <div className="text-[10.5px] font-bold tracking-[0.2em] text-brand-gold uppercase">{children}</div>;

function Preview({ doc }) {
  return (
    <div className="mt-5 rounded-xl bg-[#F4EFE4] border border-brand-gold/30 p-5 sm:p-6 space-y-5" data-testid="osservazione-preview">
      <div>
        <Overline>Una prima osservazione</Overline>
        <div className="font-serif text-[24px] text-brand-ink leading-tight mt-1">{doc.hotel_name}</div>
        <div className="text-[12.5px] text-brand-muted">{doc.locality}</div>
        {doc.cover_line && <p className="font-serif italic text-[15px] text-brand-ink/90 mt-3 leading-relaxed" data-testid="osservazione-cover-line">“{doc.cover_line}”</p>}
      </div>
      <div>
        <div className="font-serif text-[18px] text-brand-green">Prima di entrare nell’hotel</div>
        <div className="mt-2 divide-y divide-brand-gold/20">
          {(doc.observations || []).map((o, i) => (
            <div key={i} className="py-3" data-testid={`osservazione-obs-${i}`}>
              <Overline>{o.area}</Overline>
              <div className="text-[11px] font-serif italic text-brand-green mt-1">Cosa emerge</div>
              <p className="text-[13px] text-brand-ink leading-snug">{o.emerges}</p>
              {o.why && <><div className="text-[11px] font-serif italic text-brand-green mt-1.5">Perché merita attenzione</div><p className="text-[12.5px] text-brand-ink/85 leading-snug">{o.why}</p></>}
              {o.source && <div className="text-[10.5px] text-brand-muted italic mt-1">Osservato su: {o.source}</div>}
            </div>
          ))}
        </div>
      </div>
      {(doc.relations || []).length > 0 && (
        <div>
          <div className="font-serif text-[18px] text-brand-green">Il singolo dato racconta poco.</div>
          <div className="mt-2 space-y-3">
            {doc.relations.map((r, i) => (
              <div key={i} data-testid={`osservazione-rel-${i}`}>
                <div className="text-[11px] font-bold tracking-[0.15em] text-brand-green uppercase">{r.title}</div>
                <p className="text-[13px] text-brand-ink leading-snug mt-1">{r.text}</p>
                {r.sources?.length > 0 && <div className="text-[10.5px] text-brand-muted italic mt-0.5">Fonti messe in relazione: {r.sources.join(", ")}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {doc.limits && <p className="text-[11.5px] text-brand-muted italic border-t border-brand-gold/20 pt-3">{doc.limits}</p>}
    </div>
  );
}

export default function PrimaOsservazione({ hotels }) {
  const [sel, setSel] = useState(hotels[0] ? `${hotels[0].tenant}::${hotels[0].hotel}` : "");
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(false);
  const timer = useRef(null);
  const [tenant, hotel] = sel.split("::");

  const load = async () => {
    if (!tenant || !hotel) return;
    try { const d = await api.osservazione(tenant, hotel); setDoc(d); return d; } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { setDoc(null); load(); return () => clearInterval(timer.current); }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    clearInterval(timer.current);
    if (doc?.status === "pending") timer.current = setInterval(load, 5000);
    return () => clearInterval(timer.current);
  }, [doc?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async (force) => {
    setBusy(true);
    try { const d = await api.osservazioneGenerate({ tenant, hotel, force }); setDoc(d); if (d.status === "pending") toast.message("Scrittura in corso: 1-2 minuti, solo dai dati reali letti."); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const download = async () => {
    setDl(true);
    try {
      const res = await fetch(api.osservazionePdfUrl(tenant, hotel), { headers: authHeaders() });
      if (!res.ok) { let d; try { d = (await res.json()).detail; } catch { d = res.statusText; } throw new Error(d); }
      const blob = await res.blob(); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `LEUTELIA_prima_osservazione_${hotel}.pdf`; a.click();
      toast.success("PDF A5 pronto: lo trovi tra i download.");
    } catch (e) { toast.error(e.message); } finally { setDl(false); }
  };

  return (
    <section className="mt-6 bg-white rounded-xl border border-brand-gold/25 p-5 sm:p-6" data-testid="prima-osservazione">
      <div className="flex flex-wrap items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-brand-green flex items-center justify-center shrink-0"><BookOpen size={20} className="text-brand-gold" strokeWidth={1.7} /></div>
        <div className="flex-1 min-w-[260px]">
          <Overline>Documento interno LEUTELIA</Overline>
          <h3 className="font-serif text-[22px] text-brand-ink leading-tight mt-1">Prima Osservazione riservata (A5)</h3>
          <p className="text-[13px] text-brand-muted mt-1">Tre o quattro pagine per la Direzione di un hotel: osservazioni e relazioni scritte SOLO dai dati reali letti (sito, Google, foto, recensioni, social). Nessun punteggio, nessun dato inventato. Visibile solo all'amministrazione.</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <select data-testid="osservazione-hotel" value={sel} onChange={(e) => setSel(e.target.value)} className="h-10 rounded-md border border-input bg-white px-3 text-[13.5px] min-w-[260px]">
          {hotels.map((h) => <option key={`${h.tenant}::${h.hotel}`} value={`${h.tenant}::${h.hotel}`}>{h.name || h.hotel} · {h.tenant}</option>)}
        </select>
        <Button data-testid="osservazione-generate" disabled={busy || doc?.status === "pending" || !sel} onClick={() => generate(doc?.status === "ready")} className="h-10 rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[13px] font-semibold">
          {busy || doc?.status === "pending" ? <Loader2 size={15} className="mr-2 animate-spin" /> : doc?.status === "ready" ? <RefreshCw size={15} className="mr-2" /> : <Sparkles size={15} className="mr-2" />}
          {doc?.status === "pending" ? "Scrittura in corso…" : doc?.status === "ready" ? "Riscrivi" : "Scrivi la prima osservazione"}
        </Button>
        {doc?.status === "ready" && (
          <Button data-testid="osservazione-pdf" variant="outline" disabled={dl} onClick={download} className="h-10 rounded-lg border-brand-green/40 text-brand-green text-[13px] font-semibold">
            {dl ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Download size={15} className="mr-2" />} Scarica il PDF A5
          </Button>
        )}
        {doc?.status === "ready" && <span className="text-[11.5px] text-brand-muted" data-testid="osservazione-meta">scritta il {new Date(doc.generated_at).toLocaleString("it-IT")} · comprensione v{doc.understanding_version}</span>}
        {doc?.status === "failed" && <span className="text-[12px] text-brand-maroon" data-testid="osservazione-error">{doc.error}</span>}
      </div>
      {doc?.status === "ready" && <Preview doc={doc} />}
    </section>
  );
}
